import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Slider,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
} from "@mui/material";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import VideoCreationDialogForVeo from "./VideoCreationDialogForVeo";
import { useWorkspace } from "../../../../contexts/WorkspaceContext";
import CommonToast from "../../../Common_Components/CommonToast";

const TrimVideo = ({ activeVideo, veo2Videos }) => {
  const [duration, setDuration] = useState(null); // Default duration from your image
  const [isPlaying, setIsPlaying] = useState(false);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(8);
  const [activeVideoObject, setActiveVideoObject] = useState(null);
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(null);
  const [ffmpegLoading, setFfmpegLoading] = useState(false);
  const { setVeo2Videos } = useWorkspace();
  let ffmpeg = null;
  let ffmpegLoaded = false;
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState();
  const [iconType, setIconType] = useState("success");

  const handleCloseToast = () => {
    setOpen(false);
  };

  useEffect(() => {
    setActiveVideoObject(veo2Videos[activeVideo]);
    setDuration(Math.floor(veo2Videos[activeVideo].duration));
    setTrimEnd(Math.floor(veo2Videos[activeVideo].duration));
  }, [activeVideo]);

  // Play/pause toggle
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Format time for display
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const saveTrimmedVideo = () => {
    setVeo2Videos((prevVideos) =>
      prevVideos.map((video) =>
        video.id === activeVideoObject.id
          ? {
              ...video,
              url: activeVideoObject.url, // updated trimmed URL
              duration: activeVideoObject.duration, // updated duration
              trimmed: activeVideoObject.trimmed, // optional flag
            }
          : video
      )
    );

    setOpen(true);
    setContent("Video updated successfully!");
  };

  const loadFFmpeg = async () => {
    if (ffmpegLoaded && ffmpeg) return ffmpeg;

    // Initialize FFmpeg
    ffmpeg = new FFmpeg();

    // Set logger - this should work with the newer version
    ffmpeg.on("log", ({ type, message }) => {
      console.log(`[ffmpeg ${type}] ${message}`);
    });

    // Progress handling
    ffmpeg.on("progress", ({ progress, time }) => {
      console.log(`Progress: ${Math.round(progress * 100)}%`);
    });

    const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd";

    try {
      await ffmpeg.load({
        coreURL: await toBlobURL(
          `${baseURL}/ffmpeg-core.js`,
          "text/javascript"
        ),
        wasmURL: await toBlobURL(
          `${baseURL}/ffmpeg-core.wasm`,
          "application/wasm"
        ),
      });

      ffmpegLoaded = true;
      console.log("FFmpeg loaded successfully");
      return ffmpeg;
    } catch (error) {
      console.error("Failed to load FFmpeg:", error);
      throw error;
    }
  };

  // const downloadVideo = (videoBlob, fileName = "trimmed_video.mp4") => {
  //   const blobUrl = URL.createObjectURL(videoBlob);
  //   const link = document.createElement("a");
  //   link.href = blobUrl;
  //   link.download = fileName;
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  //   URL.revokeObjectURL(blobUrl);
  // };

  // const trimVideo = async (videoFile, startTime, endTime, onProgress) => {
  //   const ff = await loadFFmpeg();

  //   ff.on("progress", ({ progress }) => {
  //     if (onProgress) onProgress(progress * 100);
  //   });

  //   await ff.writeFile("input.mp4", await fetchFile(videoFile));

  //   await ff.exec([
  //     "-i",
  //     "input.mp4",
  //     "-ss",
  //     startTime.toString(),
  //     "-t",
  //     (endTime - startTime).toString(),
  //     "-c:v",
  //     "libx264",
  //     "-an", // disable audio
  //     "-movflags",
  //     "faststart", // browser-friendly
  //     "output.mp4",
  //   ]);

  //   const data = await ff.readFile("output.mp4");
  //   return new Blob([data.buffer], { type: "video/mp4" });
  // };


  const trimVideo = async (videoFile, startTime, endTime, onProgress) => {
  const ff = await loadFFmpeg();

  ff.on("progress", ({ progress }) => {
    if (onProgress) onProgress(Math.min(progress * 100, 100));
  });

  await ff.writeFile("input.mp4", await fetchFile(videoFile));

  // Optimized command for speed
  await ff.exec([
    "-i", "input.mp4",
    "-ss", startTime.toString(),
    "-t", (endTime - startTime).toString(),
    "-c:v", "libx264",
    "-preset", "ultrafast", // Fastest encoding preset
    "-crf", "28", // Higher CRF = smaller file + faster encoding
    "-an", // No audio
    "-movflags", "+faststart",
    "-threads", "2", // Limit threads for browser environment
    "output.mp4"
  ]);

  const data = await ff.readFile("output.mp4");
  return new Blob([data], { type: "video/mp4" });
};

  // Handle trim action
  const handleVeo2Trim = async (ActiveObject, startTime, endTime) => {
    setFfmpegLoading(true);
    try {
      const trimmedBlob = await trimVideo(
        ActiveObject.url,
        startTime,
        endTime,
        isProcessing
      );
      const trimmedUrl = URL.createObjectURL(trimmedBlob);

      // Update the video object
      setActiveVideoObject((prevVideo) => ({
        ...prevVideo,
        url: trimmedUrl, // Only for display, not functional!
        duration: endTime - startTime,
        trimmed: true,
      }));
      setDuration(endTime - startTime);
      setTrimStart(0)
      setOpen(true);
      setContent("Video trimmed successfully!");
    } catch (error) {
      setOpen(true);
      setIconType("error");
      setContent("failed: " + error.message);
    } finally {
      setFfmpegLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", padding: "0px" }}>
      <Card
        sx={{
          backgroundColor: "#282D51",
          display: "flex",
          justifyContent: "center",
          alignSelf: "center",
          boxShadow: "none",
          padding: "0px",
        }}
      >
        <CardContent sx={{ padding: "0px", width: "auto" }}>
          {/* Video Player */}
          <Paper
            sx={{
              mb: 3,
              overflow: "hidden",
              backgroundColor: "#282D51",
              boxShadow: "none",
            }}
          >
            <div
              style={{
                position: "relative",
                borderRadius: "8px",
                overflow: "hidden",
                marginBottom: "12px",
                bgcolor: "#282D51",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <video
                ref={videoRef}
                src={activeVideoObject?.url}
                style={{
                  width: "286px",
                  height: "161px",
                  objectFit: "contain",
                  backgroundColor: "#000",
                  borderRadius: "10px",
                }}
                preload="metadata"
                onClick={togglePlayPause}
              />
              {!isPlaying && (
                <div
                  onClick={togglePlayPause}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "60px",
                    height: "60px",
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              )}
            </div>
          </Paper>
          {/* Timeline with Trim Controls */}
          <Box sx={{ mb: 3 }}>
            {/* Main Timeline Slider */}

            {/* Trim Controls */}
            <Box sx={{ position: "relative", mb: 1 }}>
              {/* Time Markers */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  // px: 0.5,
                  mb: 1,
                }}
              >
                {[...Array(duration + 1)].map((_, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", color: "rgba(255, 255, 255, 1)" }}
                  >
                    {index}
                  </Typography>
                ))}
              </Box>

              {/* Trim Range Slider */}
              <Slider
                value={[trimStart, trimEnd]}
                min={0}
                max={duration}
                step={1}
                onChange={(event, newValue) => {
                  setTrimStart(newValue[0]);
                  setTrimEnd(newValue[1]);
                }}
                valueLabelDisplay="auto"
                valueLabelFormat={formatTime}
                sx={{
                  color: "primary.main",
                  "& .MuiSlider-thumb": {
                    backgroundColor: "#fff",
                    border: "2px solid currentColor",
                  },
                  "& .MuiSlider-track": {
                    backgroundColor: "primary.main",
                  },
                  "& .MuiSlider-rail": {
                    backgroundColor: "grey.300",
                  },
                }}
              />
            </Box>
          </Box>

          {/* Control Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* Playback Controls */}
            {/* <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={togglePlayPause} color="primary">
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <IconButton onClick={() => {
              if (videoRef.current) {
                videoRef.current.currentTime = 0;
                setCurrentTime(0);
              }
            }}>
              <Replay />
            </IconButton>
          </Box> */}

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={() => {
                  handleVeo2Trim(activeVideoObject, trimStart, trimEnd);
                }}
                // onClick={handleTrim}
                sx={{
                  color: "rgba(255, 255, 255, 1)",
                  "&:hover": { backgroundColor: "#373E6D" },
                  "&.Mui-selected": { backgroundColor: "#373E6D" },
                  py: 1.5,
                  fontSize: 14,
                  fontFamily: "Inter",
                  bgcolor: "none",
                  padding: "10px 24px",
                  border: "1px solid rgba(255, 255, 255, 0.30)",
                  height: "40px",
                  width: "80px",
                }}
              >
                Trim
              </Button>
              <Button
                onClick={saveTrimmedVideo}
                variant="contained"
                sx={{
                  padding: "10px 24px",
                  bgcolor: "#ffffff",
                  color: "#242851",
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "16px",
                  fontStyle: "normal",
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                  ":hover": { bgcolor: "#ddd" },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <VideoCreationDialogForVeo
        open={ffmpegLoading}
        onClose={() => setFfmpegLoading(false)}
        videoTitle={"Trimming Video"}
        dailogMessage={
          "Your video is being Trim. This may take a few moments — please wait."
        }
      />
      <CommonToast
        open={open}
        onClose={handleCloseToast}
        content={content}
        iconType={iconType}
        bgColor={"#373E6D"}
      />
    </Box>
  );
};

export default TrimVideo;
